import { useState, useCallback, useMemo, useEffect, Suspense, lazy } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryParams, useUser } from '@src/hooks';
import styles from './WellbeingFoundations.module.scss';
import { SllProgram, useGetProgramLazyQuery } from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import Header from 'src/components/header/Header';
import NavLayout from './navLayout/NavLayout';
import { resetGoogleTranslateWidget, sortByPosition } from '@src/utils/helpers';
import { ProgramID } from '@src/constants';


const GradeItem = lazy(() => import('./gradeItem/GradeItem'));
const StudentIntroGradeItem = lazy(() => import('./gradeItem/StudentIntroGradeItem'));

function WellbeingFoundations() {
  const [user] = useUser();
  const history = useHistory();
  const params = useQueryParams();
  const tab = params.get('tab');
  const initialMode = tab || "student-introduction";
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadOthers, setLoadOthers] = useState<number | null>(null);

  const [getProgram, {data, loading}] = useGetProgramLazyQuery({fetchPolicy: 'cache-first'});
  
  const isAUuser = useMemo(() => {return user?.id?.startsWith("AU_") ? true : false}, [user?.id]);

  useEffect(() => {
    resetGoogleTranslateWidget();
  }, [])

  // Fetch the data based on the current mode
  useEffect(() => {
    if (user) {
      let programId = isAUuser ? ProgramID.AUSSTUDENTINTRODUCTION : ProgramID.STUDENTINTRODUCTION
      if(tab === "brainbreaks") {
        setCurrentMode(tab);
        programId = isAUuser ? ProgramID.AUSBRAINBREAKS : ProgramID.BRAINBREAKS
      } else if (tab === "skillbuilders") {
        setCurrentMode(tab);
        programId = isAUuser ? ProgramID.AUSSKILLBUILDER : ProgramID.SKILLBUILDER
      } else {
        setCurrentMode('student-introduction');
        programId = isAUuser ? ProgramID.AUSSTUDENTINTRODUCTION : ProgramID.STUDENTINTRODUCTION
      }
      getProgram({ variables: { input: { id: programId } } });
    }
  }, [tab, user, isAUuser, getProgram]);

  // Update the URL parameter when the mode changes
  useEffect(() => {
    history.replace(`/wellbeingfoundations?tab=${currentMode}`);
  }, [currentMode, history]);

  // Scroll to the top of the list when the component mounts
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      setTimeout(() => {
        root.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, []);

  // Handle selection of a grade item
  const handleSelect = useCallback((gradeId: string) => {
    setSelected(selectedId !== gradeId);
    setSelectedId(selectedId !== gradeId ? gradeId : null);
  }, [selectedId]);

  // Handle deselection and scroll to the grade item
  const handleNoneSelect = (gradeId?: string) => {
    setSelected(false);
    setSelectedId(null);

    if (gradeId) {
      setTimeout(() => {
        document.getElementById(gradeId)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 500);
    }
  };

  // Sort the collections data
  const sortedCollections = useMemo(() => {
    return data?.getProgram ? [...data?.getProgram?.mcs ?? []].sort(sortByPosition) : [];
  }, [data]);

  // Render the lesson list view
  const lessonListView = (
  <div className={styles.unitsView}>
      <div id="mainScrollView" style={{ height: '100%' }}>
        <NavLayout styleMode={currentMode} program={data?.getProgram as SllProgram} handleSelectStyleMode={setCurrentMode} isAUuser={isAUuser}/>
        <div className={styles.mainView}>
          {loading ? (
            <div className={styles.spinner}><Spinner /></div>
          ) : (
            <div className={styles.libraryView}>
              {(
                sortedCollections.map((item, index) => (
                  <Suspense key={index} fallback={<div className={styles.lazyLoadingView}><Spinner /></div>}>
                    <div id={item?.id ?? ''} style={{ position: 'relative', marginBottom: 72 }}>
                      {currentMode === "student-introduction" ? 
                      <StudentIntroGradeItem
                        gradeId={item?.id ?? ''}
                        gradeName={item?.name ?? ''}
                        gradeHidden={item?.hidden ?? false}
                        selected={selected}
                        selectedId={selectedId}
                        handleNoneSelect={() => handleNoneSelect(item?.id)}
                        handleSeleted={handleSelect}
                        isAUuser={isAUuser}
                        /> 
                      : <GradeItem
                        gradeId={item?.id ?? ''}
                        gradeName={item?.name ?? ''}
                        selected={selected}
                        selectedId={selectedId}
                        handleNoneSelect={() => handleNoneSelect(item?.id)}
                        handleSeleted={handleSelect}
                        isAUuser={isAUuser}
                        loadOthers={loadOthers}
                        setLoadOthers={setLoadOthers}
                        index={index}
                      />}
                    </div>
                  </Suspense>
                )
              ))
            }
            </div>
          )}
        </div>
      </div>
  </div>);

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <div className={styles.page}>
          {lessonListView}
        </div>
      </div>
    </>
  );
}

export default WellbeingFoundations;
